import {
  IntegrationStatus,
  AvailableIntegrations,
} from "../../../consts/integrations";

// Reuses the same "needs attention" classification SyncIssuesInfo.js already
// uses for the sidebar notification — not re-derived here. There is no
// time-based "stale" concept anywhere in the app (no field compares a sync
// timestamp against an expected window), so this widget only distinguishes
// healthy vs. failed, plus the two transitional statuses shown neutrally.
const FAILED_STATUSES = [
  IntegrationStatus.syncError,
  IntegrationStatus.reAuthRequired,
  IntegrationStatus.permissionsRevoked,
];

const FAILURE_LABEL = {
  [IntegrationStatus.syncError]: "Sync failed",
  [IntegrationStatus.reAuthRequired]: "Session expired",
  [IntegrationStatus.permissionsRevoked]: "Access revoked",
};

/**
 * @param {number} status - IntegrationStatus value
 * @returns {"healthy"|"failed"|"pending"}
 */
export function getSourceLevel(status) {
  if (status === IntegrationStatus.synced) return "healthy";
  if (FAILED_STATUSES.includes(status)) return "failed";
  return "pending";
}

/**
 * @param {number} status
 * @returns {string} badge label
 */
export function getSourceStatusLabel(status) {
  if (status === IntegrationStatus.synced) return "Healthy";
  if (FAILURE_LABEL[status]) return FAILURE_LABEL[status];
  if (status === IntegrationStatus.syncInProgress) return "Sync in progress";
  return "Not synced yet";
}

/**
 * The platform's product name (e.g. "Google Calendar™") reads better than
 * `integration.name`, whose raw value is closer to "Google Calendar
 * Integration" — the exact repetitive text this widget replaces (see
 * README "Why this replaces the table"). Falls back to the stored name for
 * any integration type not in AvailableIntegrations.
 * @param {Object} integration
 */
export function getSourceName(integration) {
  const meta = AvailableIntegrations.find((a) => a.type === integration.type);
  return meta?.title || integration.name || integration.type;
}

export function formatNumber(n) {
  return new Intl.NumberFormat(window.navigator.language || "en-US").format(n);
}

const RELATIVE_UNITS = [
  ["year", 31536000],
  ["month", 2592000],
  ["week", 604800],
  ["day", 86400],
  ["hour", 3600],
  ["minute", 60],
  ["second", 1],
];

/**
 * "2 min ago", "4 days ago" — auto-picks the largest whole unit.
 * @param {number} unixSeconds
 * @returns {string}
 */
export function formatRelativeTime(unixSeconds) {
  const diff = Math.round(Date.now() / 1000 - unixSeconds);
  const rtf = new Intl.RelativeTimeFormat(
    window.navigator.language || "en-US",
    {
      numeric: "auto",
    }
  );

  for (const [unit, secondsInUnit] of RELATIVE_UNITS) {
    if (Math.abs(diff) >= secondsInUnit || unit === "second") {
      return rtf.format(-Math.round(diff / secondsInUnit), unit);
    }
  }
}
